import { useEffect, useCallback } from 'react';
import { usePTT } from '@/contexts/PTTContext';
import { useCamera } from '@/contexts/CameraContext';
import { useWebSocket } from '@/contexts/WebSocketContext';
import { useAppConfig } from '@/contexts/AppConfigContext';
import { useConsole } from '@/contexts/ConsoleContext';
import { useElevenLabs } from '@/hooks/useElevenLabs';
import { useHIDDevice } from '@/hooks/useHIDDevice';
import { useToast } from '@/hooks/use-toast';
import { PTTMode, UserMessageEvent } from '@/types';
import { ManualPTTButton } from './ManualPTTButton';

export function PTTController() {
  const { mode, setMode, startRecording, stopRecording, isRecording, isProcessing, setIsProcessing } = usePTT();
  const { captureFrame, isEnabled: cameraEnabled } = useCamera();
  const { sendMessage, isConnected, agentStatus } = useWebSocket();
  const { transcribeAudio, playTTS, stopTTS } = useElevenLabs();
  const { isAvailable: hidAvailable, onButtonPress, onButtonRelease } = useHIDDevice();
  const { toast } = useToast();
  const { updateConfig } = useAppConfig();
  const { addLog } = useConsole();

  // Set default mode based on HID availability (no auto-request)
  useEffect(() => {
    if (!hidAvailable) {
      setMode(PTTMode.MANUAL);
      console.log('PTT Mode: Manual (WebHID not available)');
    } else {
      // HID available but don't request automatically
      // User must trigger it manually to comply with user gesture requirement
      setMode(PTTMode.MANUAL);
      console.log('PTT Mode: Manual (use Settings to enable HID device)');
    }
  }, [hidAvailable, setMode]);

  // Handle recording completion
  const handleRecordingComplete = useCallback(
    async (audioBlob: Blob) => {
      try {
        // Set processing state to disable PTT
        setIsProcessing(true);

        // Transcribe audio
        const sttResult = await transcribeAudio(audioBlob);
        if (!sttResult || !sttResult.transcript) {
          console.warn('PTT: Failed to transcribe audio');
          toast({
            title: 'Voice Message Failed',
            description: 'Could not transcribe audio',
            variant: 'destructive',
          });
          return;
        }

        console.log('🎤 PTT: Transcribed:', sttResult.transcript);

        // Auto-prepend /reset if transcript contains "chào"
        let finalTranscript = sttResult.transcript;
        const transcript = sttResult.transcript.toLowerCase().trim();

        if (transcript.includes('chào')) {
          finalTranscript = `/reset ${sttResult.transcript}`;
          console.log('🔄 Auto-prepended /reset - contains "chào":', finalTranscript);
        }

        console.log('🔍 Checking keywords for transcript:', transcript);

        // Log character codes for debugging Vietnamese encoding issues
        console.log('🔤 Character analysis:', {
          transcriptLength: transcript.length,
          firstChars: transcript.substring(0, 20).split('').map(c => `${c}(${c.charCodeAt(0)})`).join(' '),
          containsCam: transcript.indexOf('cam'),
          containsCamera: transcript.indexOf('camera'),
          containsMo: transcript.indexOf('mở'),
          containsBat: transcript.indexOf('bật'),
          containsTat: transcript.indexOf('tắt')
        });

        // Check for keyword commands
        // Camera control keywords - check if transcript contains camera-related words
        const hasCameraKeyword = transcript.includes('cam') || transcript.includes('camera');
        const hasEnableKeyword = transcript.includes('mở') || transcript.includes('bật');
        const hasDisableKeyword = transcript.includes('tắt');

        console.log('📋 Keyword analysis:', {
          original: sttResult.transcript,
          lowercase: transcript,
          hasCameraKeyword,
          hasEnableKeyword,
          hasDisableKeyword,
          willExecuteCameraCommand: hasCameraKeyword && (hasEnableKeyword || hasDisableKeyword)
        });

        if (hasCameraKeyword && (hasEnableKeyword || hasDisableKeyword)) {
          const shouldEnable = hasEnableKeyword;
          const action = shouldEnable ? 'bật' : 'tắt';
          const responseText = `Em đã ${action} camera theo yêu cầu ạ`;

          console.log('📷 ✅ Camera command DETECTED - Executing:', {
            transcript,
            shouldEnable,
            action,
            hasCameraKeyword,
            hasEnableKeyword,
            hasDisableKeyword
          });

          // Execute camera action by updating config (CameraContext will handle enable/disable)
          updateConfig({ cameraEnabled: shouldEnable });

          // Play TTS response
          await playTTS(responseText);

          // Add to chat log (but don't send to agent)
          const timestamp = Date.now();
          addLog({
            timestamp,
            type: 'system',
            content: responseText,
          });

          console.log('📷 Camera control:', responseText);
          toast({
            title: 'Camera Control',
            description: responseText,
          });
          return;
        }

        // Stop keywords
        const hasStopKeyword = transcript.includes('thôi') || transcript.includes('dừng');

        console.log('📋 Stop keyword analysis:', {
          transcript,
          hasStopKeyword,
          willExecuteStopCommand: hasStopKeyword
        });

        if (hasStopKeyword) {
          console.log('🛑 ✅ Stop command DETECTED - Executing:', transcript);

          // Stop any ongoing TTS
          stopTTS();

          const timestamp = Date.now();

          // If agent is busy, send stop command
          if (agentStatus === 'busy') {
            const stopMessage: UserMessageEvent = {
              content: `\\stop ${sttResult.transcript}`,
              attachments: undefined,
              metadata: {
                pttMode: mode,
                confidence: sttResult.confidence,
                audioLength: audioBlob.size,
              },
              timestamp,
            };
            sendMessage(stopMessage);

            // Add to chat log
            addLog({
              timestamp,
              type: 'user',
              content: `\\stop ${sttResult.transcript}`,
            });

            console.log('🛑 Stop command sent to agent');
          } else {
            // Just add stop message to chat (no agent emit)
            addLog({
              timestamp,
              type: 'system',
              content: 'Stopped audio playback',
            });
          }

          // Show toast
          toast({
            title: 'Stopped',
            description: agentStatus === 'busy' ? 'Sent stop command to agent' : 'Stopped audio playback',
          });
          return;
        }

        // Normal processing - No keyword command detected
        console.log('📤 No keyword command detected - Sending as normal message');

        // Capture camera frame if enabled
        let cameraCapture = null;
        if (cameraEnabled) {
          cameraCapture = await captureFrame();
        }

        // Build user message
        const message: UserMessageEvent = {
          content: finalTranscript,
          attachments: cameraCapture
            ? [
                {
                  type: 'image/base64',
                  content: cameraCapture.dataUrl,
                  metadata: {
                    width: cameraCapture.width,
                    height: cameraCapture.height,
                    captureTime: cameraCapture.timestamp,
                  },
                },
              ]
            : undefined,
          metadata: {
            pttMode: mode,
            confidence: sttResult.confidence,
            audioLength: audioBlob.size,
          },
          timestamp: Date.now(),
        };

        // Add to chat log
        addLog({
          timestamp: message.timestamp,
          type: 'user',
          content: finalTranscript,
        });

        // Send to WebSocket
        sendMessage(message);

        console.log('PTT: Message sent -', finalTranscript.substring(0, 50));

        // Show success toast
        toast({
          title: 'Voice Message Sent',
          description: 'Successfully transcribed and sent',
        });
      } catch (error) {
        console.error('PTT: Failed to process recording:', error);
        toast({
          title: 'Voice Message Failed',
          description: 'Failed to process recording',
          variant: 'destructive',
        });
      } finally {
        // Always reset processing state
        setIsProcessing(false);
      }
    },
    [transcribeAudio, cameraEnabled, captureFrame, mode, sendMessage, agentStatus, toast, playTTS, stopTTS, updateConfig, addLog, setIsProcessing]
  );

  // HID mode handlers
  useEffect(() => {
    if (mode !== PTTMode.HID) return;

    onButtonPress(() => {
      console.log('HID button pressed');
      startRecording();
    });

    onButtonRelease(async () => {
      console.log('HID button released');
      const audioBlob = await stopRecording();
      if (audioBlob) {
        await handleRecordingComplete(audioBlob);
      }
    });
  }, [mode, startRecording, stopRecording, handleRecordingComplete, onButtonPress, onButtonRelease]);

  // Manual mode handlers
  const handleManualStart = useCallback(() => {
    if (isProcessing) {
      console.warn('PTT: Cannot record - Processing STT');
      return;
    }
    if (!isConnected || agentStatus === 'busy') {
      console.warn('PTT: Cannot record -', agentStatus === 'busy' ? 'Agent is busy' : 'Not connected');
      return;
    }
    startRecording();
  }, [isConnected, agentStatus, startRecording, isProcessing]);

  const handleManualStop = useCallback(async () => {
    const audioBlob = await stopRecording();
    if (audioBlob) {
      await handleRecordingComplete(audioBlob);
    }
  }, [stopRecording, handleRecordingComplete]);

  // Global Space key handler for PTT - Toggle mode (press once to start, press again to stop)
  useEffect(() => {
    let isKeyPressed = false; // Track if key is currently pressed to prevent repeat events

    const handleKeyDown = (e: KeyboardEvent) => {
      // Handle ESC key - Stop TTS and send /stop if agent is busy
      if (e.code === 'Escape') {
        const target = e.target as HTMLElement;
        const isInputFocused = target.tagName === 'INPUT' ||
                              target.tagName === 'TEXTAREA' ||
                              target.isContentEditable;

        if (isInputFocused) {
          console.log('⌨️ ESC key ignored - input focused');
          return;
        }

        e.preventDefault();
        console.log('⌨️ ESC key pressed:', { agentStatus });

        // Stop any ongoing TTS
        stopTTS();

        // If agent is busy, send stop command
        if (agentStatus === 'busy') {
          const timestamp = Date.now();
          const stopMessage: UserMessageEvent = {
            content: '/stop',
            attachments: undefined,
            metadata: {
              pttMode: mode,
              confidence: 1.0,
              audioLength: 0,
            },
            timestamp,
          };
          sendMessage(stopMessage);

          // Add to chat log
          addLog({
            timestamp,
            type: 'user',
            content: '/stop',
          });

          console.log('⌨️ ✅ ESC: Stopped TTS and sent /stop to agent');
          toast({
            title: 'Stopped',
            description: 'Stopped TTS and sent stop command to agent',
          });
        } else {
          console.log('⌨️ ✅ ESC: Stopped TTS');
          toast({
            title: 'Stopped',
            description: 'Stopped audio playback',
          });
        }
        return;
      }

      // Only handle Space key, and prevent repeat events when holding key
      if (e.code === 'Space' && !isKeyPressed) {
        const target = e.target as HTMLElement;
        const isInputFocused = target.tagName === 'INPUT' ||
                              target.tagName === 'TEXTAREA' ||
                              target.isContentEditable;

        if (isInputFocused) {
          console.log('⌨️ Space key ignored - input focused');
          return;
        }

        // Prevent default and mark key as pressed
        e.preventDefault();
        isKeyPressed = true;

        console.log('⌨️ Space key pressed (toggle):', {
          isRecording,
          isConnected,
          agentStatus
        });

        // Toggle recording state
        if (!isRecording) {
          // Start recording
          if (isProcessing) {
            console.warn('⌨️ ❌ PTT: Cannot record - Processing STT');
            return;
          }
          if (!isConnected || agentStatus === 'busy') {
            console.warn('⌨️ ❌ PTT: Cannot record -', agentStatus === 'busy' ? 'Agent is busy' : 'Not connected');
            return;
          }
          console.log('⌨️ ✅ Starting recording via Space key (toggle mode)');
          startRecording();
        } else {
          // Stop recording
          console.log('⌨️ ✅ Stopping recording via Space key (toggle mode)');
          stopRecording().then(audioBlob => {
            if (audioBlob) {
              handleRecordingComplete(audioBlob);
            }
          });
        }
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      // Reset key pressed flag when key is released
      if (e.code === 'Space') {
        isKeyPressed = false;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [isRecording, isConnected, agentStatus, startRecording, stopRecording, handleRecordingComplete, isProcessing, stopTTS, sendMessage, mode, addLog, toast]);

  // Render manual button only in manual mode
  if (mode === PTTMode.MANUAL) {
    return (
      <ManualPTTButton
        isListening={isRecording}
        isProcessing={isProcessing}
        onStartRecording={handleManualStart}
        onStopRecording={handleManualStop}
        disabled={!isConnected || agentStatus === 'busy' || isProcessing}
      />
    );
  }

  // HID mode doesn't need UI
  return null;
}
